import React, { useState, useEffect } from 'react';
import {
  Users,
  Plus,
  Search,
  Edit,
  Trash2,
  Phone,
  Mail,
  MapPin,
  Briefcase,
  DollarSign,
  Loader2,
  User,
  Link as LinkIcon,
} from 'lucide-react';
import { customerAPI, customerVehicleAPI } from '../api/client';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import AddressAutocomplete from '../components/AddressAutocomplete';
import { toast } from 'sonner';

const SERVICE_TYPES = [
  'Car Lockout',
  'Car Key Replacement',
  'Car Key Programming',
  'Ignition Repair',
  'Ignition Replacement',
  'House Lockout',
  'Lock Rekey',
  'Lock Installation',
  'Deadbolt Installation',
  'Safe Opening',
  'Commercial Lockout',
  'Other',
];

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    location_lat: null,
    location_lng: null,
    preferred_vehicle_type: '',
    notes: '',
    // Initial-job fields (optional)
    job_title: '',
    job_service_type: '',
    job_description: '',
    job_priority: 'normal',
    job_labor_cost: '',
  });

  useEffect(() => {
    loadCustomers();
  }, []);

  useEffect(() => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      setFilteredCustomers(
        customers.filter(
          (c) =>
            c.first_name?.toLowerCase().includes(query) ||
            c.last_name?.toLowerCase().includes(query) ||
            c.email?.toLowerCase().includes(query) ||
            c.phone?.includes(query)
        )
      );
    } else {
      setFilteredCustomers(customers);
    }
  }, [customers, searchQuery]);

  const loadCustomers = async () => {
    setLoading(true);
    try {
      const data = await customerAPI.list();
      setCustomers(data || []);
    } catch (error) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (customer = null) => {
    if (customer) {
      setEditingCustomer(customer);
      setFormData({
        first_name: customer.first_name || '',
        last_name: customer.last_name || '',
        email: customer.email || '',
        phone: customer.phone || '',
        address: customer.address || '',
        city: customer.city || '',
        state: customer.state || '',
        zip_code: customer.zip_code || '',
        notes: customer.notes || '',
      });
    } else {
      setEditingCustomer(null);
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        zip_code: '',
        location_lat: null,
        location_lng: null,
        preferred_vehicle_type: '',
        notes: '',
        job_title: '',
        job_service_type: '',
        job_description: '',
        job_priority: 'normal',
        job_labor_cost: '',
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.first_name || !formData.last_name || !formData.phone) {
      toast.error('Name and phone are required');
      return;
    }

    setSubmitting(true);
    try {
      const payload = { ...formData };
      if (!payload.email) delete payload.email;

      if (editingCustomer) {
        // Strip job-* fields on edit
        ['job_title', 'job_service_type', 'job_description', 'job_priority', 'job_labor_cost'].forEach(
          (k) => delete payload[k],
        );
        await customerAPI.update(editingCustomer.id, payload);
        toast.success('Customer updated!');
      } else {
        // If any job-* field is filled, create customer + initial job together
        const hasInitialJob = !!(formData.job_title || formData.job_service_type);
        if (hasInitialJob) {
          // Cast labor cost
          payload.job_labor_cost = formData.job_labor_cost ? parseFloat(formData.job_labor_cost) : 0;
          const result = await customerAPI.createWithJob(payload);
          toast.success(
            result.job
              ? `Customer added · Job #${result.job.id.slice(0, 6)} created in 'pending' status`
              : 'Customer added!',
          );
        } else {
          // Strip job fields if no initial job
          ['job_title', 'job_service_type', 'job_description', 'job_priority', 'job_labor_cost'].forEach(
            (k) => delete payload[k],
          );
          await customerAPI.create(payload);
          toast.success('Customer added!');
        }
      }
      setDialogOpen(false);
      loadCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save customer');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await customerAPI.delete(deleteConfirm.id);
      toast.success('Customer deleted!');
      setDeleteConfirm(null);
      loadCustomers();
    } catch (error) {
      toast.error('Failed to delete customer');
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

  const totalRevenue = customers.reduce((sum, c) => sum + (c.total_spent || 0), 0);
  const totalJobs = customers.reduce((sum, c) => sum + (c.total_jobs || 0), 0);

  return (
    <div className="space-y-6 fade-in" data-testid="customers-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold mb-2">Customer Management (CRM)</h1>
          <p className="text-muted-foreground">Manage your customer database and history</p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="btn-gradient-primary"
          data-testid="add-customer-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Customer
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="stat-pink border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Total Customers</p>
                <p className="text-2xl font-bold">{customers.length}</p>
              </div>
              <Users className="w-8 h-8 text-pink-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="stat-blue border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Total Jobs</p>
                <p className="text-2xl font-bold">{totalJobs}</p>
              </div>
              <Briefcase className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="stat-green border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Total Revenue</p>
                <p className="text-2xl font-bold">{formatCurrency(totalRevenue)}</p>
              </div>
              <DollarSign className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, email, or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="customer-search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Customer Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <div className="h-20 shimmer rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredCustomers.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Users className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No customers found</h3>
            <p className="text-muted-foreground mb-4">
              {searchQuery ? 'Try a different search' : 'Add your first customer to get started'}
            </p>
            <Button onClick={() => handleOpenDialog()} className="btn-gradient-primary">
              <Plus className="w-4 h-4 mr-2" />
              Add Customer
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCustomers.map((customer) => (
            <Card
              key={customer.id}
              className="card-hover"
              data-testid={`customer-card-${customer.id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                    {customer.first_name?.[0]}{customer.last_name?.[0]}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg">
                      {customer.first_name} {customer.last_name}
                    </h3>
                    {customer.total_jobs > 0 && (
                      <Badge variant="secondary" className="mt-1">
                        {customer.total_jobs} jobs
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="space-y-2 text-sm mb-4">
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <a href={`tel:${customer.phone}`} className="hover:text-primary">
                      {customer.phone}
                    </a>
                  </div>
                  {customer.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-muted-foreground" />
                      <a href={`mailto:${customer.email}`} className="hover:text-primary truncate">
                        {customer.email}
                      </a>
                    </div>
                  )}
                  {(customer.city || customer.state) && (
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-muted-foreground" />
                      <span className="text-muted-foreground">
                        {[customer.city, customer.state].filter(Boolean).join(', ')}
                      </span>
                    </div>
                  )}
                </div>

                {customer.total_spent > 0 && (
                  <div className="bg-muted/50 rounded-lg p-2 mb-3">
                    <p className="text-xs text-muted-foreground">Total Spent</p>
                    <p className="font-semibold text-green-500">
                      {formatCurrency(customer.total_spent)}
                    </p>
                  </div>
                )}

                <div className="flex gap-2 pt-3 border-t">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleOpenDialog(customer)}
                    className="flex-1"
                    data-testid={`edit-customer-${customer.id}`}
                  >
                    <Edit className="w-3 h-3 mr-1" />
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      try {
                        const { portal_path } = await customerVehicleAPI.getPortalLink(customer.id);
                        const url = `${window.location.origin}${portal_path}`;
                        await navigator.clipboard.writeText(url);
                        toast.success('Portal link copied');
                      } catch (e) { toast.error('Could not get portal link'); }
                    }}
                    data-testid={`portal-link-${customer.id}`}
                    title="Copy public portal link"
                  >
                    <LinkIcon className="w-3 h-3" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setDeleteConfirm(customer)}
                    className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCustomer ? 'Edit Customer' : 'Add New Customer'}</DialogTitle>
            <DialogDescription>
              {editingCustomer ? 'Update customer information' : 'Add a new customer to your database'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>First Name *</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  required
                  data-testid="customer-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Last Name *</Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  required
                  data-testid="customer-last-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Phone *</Label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="555-1234"
                  required
                  data-testid="customer-phone"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@example.com"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Address</Label>
                <AddressAutocomplete
                  value={formData.address}
                  onChange={(v) => setFormData({ ...formData, address: v })}
                  onSelect={(r) => setFormData({
                    ...formData,
                    address: [r.house_number, r.road].filter(Boolean).join(' ') || r.address,
                    city: r.city || formData.city,
                    state: r.state || formData.state,
                    zip_code: r.postcode || formData.zip_code,
                    location_lat: r.lat,
                    location_lng: r.lng,
                  })}
                  placeholder="Start typing an address — suggestions will appear..."
                  testid="customer-address-autocomplete"
                />
              </div>
              <div className="space-y-2">
                <Label>City</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>State</Label>
                <Input
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>ZIP Code</Label>
                <Input
                  value={formData.zip_code}
                  onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Preferred Vehicle Type</Label>
                <Select
                  value={formData.preferred_vehicle_type}
                  onValueChange={(v) => setFormData({ ...formData, preferred_vehicle_type: v })}
                >
                  <SelectTrigger data-testid="customer-vehicle-type-select">
                    <SelectValue placeholder="Select type..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="car">Car</SelectItem>
                    <SelectItem value="suv">SUV</SelectItem>
                    <SelectItem value="truck">Truck</SelectItem>
                    <SelectItem value="motorcycle">Motorcycle</SelectItem>
                    <SelectItem value="commercial">Commercial Vehicle</SelectItem>
                    <SelectItem value="residential">Residential (House)</SelectItem>
                    <SelectItem value="commercial_building">Commercial Building</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Customer notes, preferences, key codes..."
                  rows={3}
                />
              </div>
            </div>

            {/* Initial Job section — only shown on create */}
            {!editingCustomer && (
              <div className="rounded-xl border border-cyan-500/30 bg-cyan-500/5 p-4 space-y-3" data-testid="initial-job-section">
                <div className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-cyan-400" />
                  <h4 className="font-semibold text-cyan-300">Initial Job <span className="text-xs font-normal text-muted-foreground">(optional — auto-creates a pending job for this customer)</span></h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Service Type</Label>
                    <Select
                      value={formData.job_service_type}
                      onValueChange={(v) => setFormData({
                        ...formData,
                        job_service_type: v,
                        job_title: formData.job_title || v,
                      })}
                    >
                      <SelectTrigger data-testid="initial-job-service-type">
                        <SelectValue placeholder="Select service..." />
                      </SelectTrigger>
                      <SelectContent>
                        {SERVICE_TYPES.map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Job Title</Label>
                    <Input
                      value={formData.job_title}
                      onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                      placeholder="e.g. Car key replacement at 123 Main St"
                      data-testid="initial-job-title"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Priority</Label>
                    <Select
                      value={formData.job_priority}
                      onValueChange={(v) => setFormData({ ...formData, job_priority: v })}
                    >
                      <SelectTrigger data-testid="initial-job-priority">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Estimated Labor ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.job_labor_cost}
                      onChange={(e) => setFormData({ ...formData, job_labor_cost: e.target.value })}
                      placeholder="0.00"
                      data-testid="initial-job-labor"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label>Job Description</Label>
                    <Textarea
                      rows={2}
                      value={formData.job_description}
                      onChange={(e) => setFormData({ ...formData, job_description: e.target.value })}
                      placeholder="What needs to be done..."
                      data-testid="initial-job-description"
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Tip: once created, the job starts at <b>pending</b>. Click <b>Progress →</b> on the Jobs page to move it through Dispatch → Tech → Invoice → Paid.
                </p>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-gradient-primary" disabled={submitting} data-testid="customer-dialog-submit">
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : editingCustomer ? (
                  'Update Customer'
                ) : (
                  'Add Customer'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Customer</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {deleteConfirm?.first_name} {deleteConfirm?.last_name}?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Cancel
            </Button>
            <Button onClick={handleDelete} className="btn-gradient-danger">
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
